import React from "react";
import { Segmented, Slider, Switch } from "antd";
import {
  RiMap2Line,
  RiShapesLine,
  RiImageLine,
  RiGridLine,
  RiShapeLine,
} from "react-icons/ri";
import TextAction from "./TextAction";
import { getStationColor } from "../../FloorPlanModal/utils/stationUtils";
import { TRIPWIRE_ACTIVE_COLOR } from "../../FloorPlanModal/utils/tripwireColors";
import { DIVIDER, INK_SECONDARY, neutralText } from "./layerStyle";

/**
 * The 圖層 panel: 底圖 (平面圖, and 地圖 with its style and 不透明度 once the
 * site has a usable map), then 顯示 (the host's overlays:
 * 基站, 物件, 機具 or 可行區域, 越線), then 恢復預設.
 * Rows never come and go with the data: an overlay with nothing to show is a
 * disabled row, so the panel does not jump when 自動切換 changes site.
 */

// The same marks CanvasLegend uses for each overlay.
const dot = (color, size = 10) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundColor: color,
      display: "inline-block",
    }}
  />
);
const SWATCHES = {
  基站: dot(getStationColor("定位基站").primary),
  物件: <RiShapesLine style={{ color: "#595959", fontSize: 14 }} />,
  機具: (
    <span
      style={{
        width: 0,
        height: 0,
        borderLeft: "5px solid transparent",
        borderRight: "5px solid transparent",
        borderBottom: "9px solid #722ed1",
      }}
    />
  ),
  // The editor's own marks: its 可行區域 tool icon, and a 越線 stroke.
  可行區域: <RiShapeLine style={{ color: "#595959", fontSize: 14 }} />,
  越線: (
    <span
      style={{
        width: 14,
        height: 3,
        borderRadius: 2,
        backgroundColor: TRIPWIRE_ACTIVE_COLOR,
        transform: "rotate(-35deg)",
      }}
    />
  ),
  平面圖: <RiImageLine style={{ color: "#595959", fontSize: 14 }} />,
  地圖: <RiMap2Line style={{ color: "#595959", fontSize: 14 }} />,
  網格: <RiGridLine style={{ color: "#595959", fontSize: 14 }} />,
};

const sectionTitle = {
  fontSize: 12,
  color: INK_SECONDARY,
  margin: "0 0 4px",
};

const Section = ({ title, children }) => (
  <section style={{ padding: "10px 12px" }}>
    <h4 style={sectionTitle}>{title}</h4>
    {children}
  </section>
);

/** One switch row: mark, name, optional count and host hint, switch. */
const SwitchRow = ({ label, checked, onChange, disabled, count, hint }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 32,
      opacity: disabled ? 0.45 : 1,
    }}
  >
    <span
      aria-hidden="true"
      style={{ width: 16, display: "inline-flex", justifyContent: "center" }}
    >
      {SWATCHES[label]}
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>
      {label}
      {count != null && (
        <span
          data-testid={`layer-count-${label}`}
          style={{
            marginLeft: 6,
            color: INK_SECONDARY,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </span>
      )}
      {hint && <span style={{ marginLeft: 6 }}>{hint}</span>}
    </span>
    <Switch
      size="small"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  </div>
);

/** 不透明度 under a base layer: 0-100 % in steps of 10, as stored. */
const OpacityRow = ({ label, value, onChange, disabled }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "0 0 4px 24px",
    }}
  >
    <span style={{ fontSize: 12, color: INK_SECONDARY }}>不透明度</span>
    <Slider
      ariaLabelForHandle={`${label}不透明度`}
      style={{ flex: 1, margin: "6px 4px" }}
      min={0}
      max={100}
      step={10}
      value={value}
      disabled={disabled}
      onChange={onChange}
      tooltip={{ formatter: (v) => `${v}%` }}
    />
    <span
      data-testid={`opacity-${label}`}
      style={{
        width: 34,
        textAlign: "right",
        fontSize: 12,
        color: INK_SECONDARY,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}%
    </span>
  </div>
);

const BaseLayers = ({ layers }) => (
  <Section title="底圖">
    <SwitchRow
      label="平面圖"
      checked={layers.planVisible}
      onChange={layers.togglePlan}
    />
    {layers.available && (
      <>
        <OpacityRow
          label="平面圖"
          value={layers.planPct}
          onChange={layers.setPlanPct}
          disabled={!layers.planVisible}
        />
        <SwitchRow
          label="地圖"
          checked={layers.mapVisible}
          onChange={layers.toggleMap}
        />
        {layers.tileLayers.length > 1 && (
          <div style={{ padding: "2px 0 6px 24px" }}>
            <Segmented
              size="small"
              block
              value={layers.layerId}
              onChange={layers.setLayerId}
              disabled={!layers.mapVisible}
              options={layers.tileLayers.map((l) => ({
                label: l.name,
                value: l.id,
              }))}
            />
          </div>
        )}
        <OpacityRow
          label="地圖"
          value={layers.mapPct}
          onChange={layers.setMapPct}
          disabled={!layers.mapVisible}
        />
      </>
    )}
    <SwitchRow
      label="網格"
      checked={layers.gridVisible}
      onChange={layers.toggleGrid}
    />
  </Section>
);

const divider = { borderTop: `1px solid ${DIVIDER}` };

/**
 * @param {object[]} toggles  {label, count, visible, onToggle, hint?} per overlay
 * @param {object} [layers]   useMapLayers(); 底圖 shows only on a plane site
 * @param {boolean} isDefault nothing to restore
 * @param {Function} onReset  恢復預設
 */
const LayerPanel = ({ toggles, layers, isDefault, onReset }) => (
  <div
    role="dialog"
    aria-label="圖層選項"
    style={{ ...neutralText, width: 248 }}
  >
    {layers?.hasPlan && <BaseLayers layers={layers} />}
    {toggles.length > 0 && (
      <div style={layers?.hasPlan ? divider : undefined}>
        <Section title="顯示">
          {toggles.map((t) => (
            <SwitchRow
              key={t.label}
              label={t.label}
              count={t.count}
              hint={t.hint}
              checked={t.visible}
              disabled={!t.count}
              onChange={t.onToggle}
            />
          ))}
        </Section>
      </div>
    )}
    <div
      style={{
        ...divider,
        padding: "8px 12px",
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <TextAction onClick={onReset} disabled={isDefault}>
        恢復預設
      </TextAction>
    </div>
  </div>
);

export default LayerPanel;
